import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import wordsprintwhite from '../pics/wordsprintwhite.png';
import randomWords from "random-words";
import { formatTime } from "../utils/timeFormatter.js";
import phone_timer from '../phone_timer.mp3';

const EndlessPractice = () => {
  const [tab, setTab] = useState("words");
  const [currentWord, setCurrentWord] = useState("");
  const [currentParagraphIndex, setCurrentParagraphIndex] = useState(0);
  const [paragraphs, setParagraphs] = useState([]);
  const [input, setInput] = useState("");
  const [elapsedTime, setElapsedTime] = useState(0);
  const [wpm, setWpm] = useState(0);
  const [accuracy, setAccuracy] = useState(100);
  const [isActive, setIsActive] = useState(false);
  const [difficulty, setDifficulty] = useState("normal");
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [textSize, setTextSize] = useState("xl");

  const [soundVal, setSoundVal] = useState(true);
  // const [theme, setTheme] = useState(() => {
  //   // Check if user has a theme preference in localStorage
  //   const savedTheme = localStorage.getItem("theme");
  //   if (savedTheme) return savedTheme;
    
  //   // Check system preference
  //   if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
  //     return "dark";
  //   }
    
  //   // Default to light theme
  //   return "light";
  // });
  const [theme, setTheme] = useState("light");
  const [showTutorial, setShowTutorial] = useState(false);
  const inputRef = useRef(null);

  // Stats
  const [correct, setCorrect] = useState(0);
  const [incorrect, setIncorrect] = useState(0);
  const [totalChars, setTotalChars] = useState(0);
  const [correctChars, setCorrectChars] = useState(0);
  const [streak, setStreak] = useState(0);
  const [maxStreak, setMaxStreak] = useState(() => {
    // Get best streak from localStorage
    const saved = localStorage.getItem("bestStreak");
    return saved ? parseInt(saved) : 0;
  });
  const [wrongWords, setWrongWords] = useState([]);
  const [sessionHistory, setSessionHistory] = useState([]);

  const navigate = useNavigate();
  const home = () => navigate("/");

  // Save best streak to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem("bestStreak", maxStreak.toString());
  }, [maxStreak]);

  // Format paragraph with special characters, numbers, and punctuation
  const formatParagraph = (text) => {
    let words = text.split(" ");

    // Capitalize first word
    words[0] = words[0].charAt(0).toUpperCase() + words[0].slice(1);

    // Add a number (4-6 digits)
    const numIndex = Math.floor(Math.random() * words.length);
    words[numIndex] += " " + (1000 + Math.floor(Math.random() * 900000));

    // Add one word wrapped in ()
    const parenIndex = Math.floor(Math.random() * words.length);
    words[parenIndex] = `(${words[parenIndex]})`;

    // Add one word wrapped in ""
    const quoteIndex = Math.floor(Math.random() * words.length);
    words[quoteIndex] = `"${words[quoteIndex]}"`;

    // Add a special character to one word
    const specialChars = ["?", ":", ";"];
    const specialIndex = Math.floor(Math.random() * words.length);
    words[specialIndex] += specialChars[Math.floor(Math.random() * specialChars.length)];

    // Add a comma after a random word
    const commaIndex = Math.floor(Math.random() * (words.length - 1));
    words[commaIndex] += ",";

    // Build sentences: break every ~10-15 words into sentences
    let sentences = [];
    for (let i = 0; i < words.length; i += 12) {
      let chunk = words.slice(i, i + 12).join(" ");
      chunk = chunk.charAt(0).toUpperCase() + chunk.slice(1);
      if (!chunk.endsWith(".")) chunk += ".";
      sentences.push(chunk);
    }

    return sentences.join(" ");
  };

  // Generate quotes for quote mode
  const generateQuotes = () => {
    const quotes = [
      "The only way to do great work is to love what you do. - Steve Jobs",
      "Innovation distinguishes between a leader and a follower. - Steve Jobs",
      "The future belongs to those who believe in the beauty of their dreams. - Eleanor Roosevelt",
      "The way to get started is to quit talking and begin doing. - Walt Disney",
      "Life is what happens when you're busy making other plans. - John Lennon",
      "Spread love everywhere you go. - Mother Teresa",
      "The only thing we have to fear is fear itself. - Franklin D. Roosevelt",
      "Do one thing every day that scares you. - Eleanor Roosevelt",
      "Well done is better than well said. - Benjamin Franklin",
      "The best and most beautiful things in the world cannot be seen or even touched - they must be felt with the heart. - Helen Keller"
    ];
    return quotes;
  };

  useEffect(() => {
    setCurrentWord(getNextWord());
    setParagraphs(
      Array.from({ length: 10 }, () =>
        formatParagraph(randomWords({ min: 10, max: 20, join: " " }))
      )
    );
  }, []);

  // Timer
  useEffect(() => {
    let interval = null;
    if (isActive) {
      interval = setInterval(() => {
        setElapsedTime((time) => time + 1);
      }, 1000);
    } else if (!isActive && elapsedTime !== 0) {
      clearInterval(interval);
    }
    return () => clearInterval(interval);
  }, [isActive, elapsedTime]);

  // Calculate WPM based on correct characters and time
  useEffect(() => {
    if (elapsedTime > 0) {
      const minutes = elapsedTime / 60;
      // Standard WPM calculation: (characters typed / 5) / minutes
      const wordsTyped = correctChars / 5;
      setWpm(Math.round(wordsTyped / minutes));
    }
  }, [elapsedTime, correctChars]);

  useEffect(() => {
    if (inputRef.current) inputRef.current.focus();
  }, [tab, currentWord, currentParagraphIndex]);

  const handleInputChange = (e) => {

      let value = e.target.value;

  if (tab === "words") {
    // 1. prevent starting with space
    if (value.startsWith(" ")) return;

    // 2. if user typed a space at the end, submit word immediately
    if (value.endsWith(" ")) {
      handleSubmit();
      return; // stop further updates, clear will happen in handleSubmit
    }
  }

    
    if (!isActive && e.target.value.trim() !== "") setIsActive(true);
    setInput(e.target.value);
    
    // Update accuracy in real-time for better UX
    if (tab === "words") {
      const target = currentWord;
      const isCorrect = target.startsWith(e.target.value);
      setAccuracy(isCorrect ? 100 : 0);
    }
  };

  const handleSubmit = (e) => {
    if (e) e.preventDefault();
    if (!input.trim()) return;
    
    const target = tab === "words" ? currentWord : 
                   tab === "paragraphs" ? paragraphs[currentParagraphIndex] : 
                   tab === "quotes" ? paragraphs[currentParagraphIndex] : "";
    
    if (tab === "words") {
      // Word mode: simple match
      const isCorrect = input.trim() === target.trim();
      
      if (isCorrect) {
        setCorrect(prev => prev + 1);
        setCorrectChars(prev => prev + target.length);
        updateStreak(true);
      } else {
        setIncorrect(prev => prev + 1);
        setWrongWords(prev => [...prev, input.trim()]);
        updateStreak(false);
      }
      
      setTotalChars(prev => prev + target.length);
      setCurrentWord(getNextWord());
      
      playSound();
      
    } else {
      // Paragraph/Quote mode: character-level checking for better accuracy
      const targetText = target.trim();
      const typedText = input.trim();
      
      let localCorrectChars = 0;
      for (let i = 0; i < Math.min(typedText.length, targetText.length); i++) {
        if (typedText[i] === targetText[i]) {
          localCorrectChars++;
        }
      }
      
      setCorrectChars(prev => prev + localCorrectChars);
      setTotalChars(prev => prev + typedText.length);
      
      // For word count, count the words that were fully correct
      const typedWords = typedText.split(/\s+/);
      const targetWords = targetText.split(/\s+/);
      
      let localCorrectWords = 0;
      let localIncorrectWords = 0;
      
      typedWords.forEach((word, i) => {
        if (i < targetWords.length && word === targetWords[i]) {
          localCorrectWords++;
          updateStreak(true);
        } else if (word) {
          localIncorrectWords++;
          updateStreak(false);
          setWrongWords(prev => [...prev, word]);
        }
      });
      
      setCorrect(prev => prev + localCorrectWords);
      setIncorrect(prev => prev + localIncorrectWords);
      setCurrentParagraphIndex(prev => (prev + 1) % paragraphs.length);

      playSound();
    }
    
    // Update accuracy
    const newAccuracy = totalChars > 0 ? Math.round((correctChars / totalChars) * 100) : 100;
    setAccuracy(newAccuracy);
    
    setInput("");
  };

  const playSound = () => {
    if (soundVal) new Audio(phone_timer).play();
  };
  
  const updateStreak = (isCorrect) => {
    if (isCorrect) {
      setStreak((prev) => {
        const newStreak = prev + 1;
        if (newStreak > maxStreak) setMaxStreak(newStreak);
        return newStreak;
      });
    } else {
      setStreak(0);
    }
  };

  const checkWords = () => {    
    const target = tab === "words" ? currentWord : 
                   tab === "paragraphs" ? paragraphs[currentParagraphIndex] : 
                   tab === "quotes" ? paragraphs[currentParagraphIndex] : "";
    return target.trim().startsWith(input.trim());
  };

  const getNextWord = () => {
    if (difficulty === "easy") {
      return randomWords({ exactly: 1 })[0].toLowerCase();
    }

    if (difficulty === "normal") {
      let word = randomWords({ exactly: 1 })[0];
      word = word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();

      if (Math.random() < 0.3) {
        word += Math.floor(Math.random() * 1000);
      }

      return word;
    }

    if (difficulty === "hard") {
      let word = randomWords({ exactly: 1 })[0];

      if (Math.random() < 0.4) {
        word = word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
      }

      if (Math.random() < 0.3) {
        word += Math.floor(Math.random() * 1000);
      }

      const wrappers = [
        (txt) => `"${txt}"`,
        (txt) => `'${txt}'`,
        (txt) => `(${txt})`,
      ];
      word = wrappers[Math.floor(Math.random() * wrappers.length)](word);

      const endings = [".", ",", ";", ":", "!", "?"];
      if (Math.random() < 0.5) {
        word += endings[Math.floor(Math.random() * endings.length)];
      }

      return word;
    }
  };

  const resetPractice = () => {
    // Save session history
    if (elapsedTime > 5) { // Only save sessions longer than 5 seconds
      setSessionHistory(prev => [...prev, {
        date: new Date().toLocaleString(),
        wpm,
        accuracy,
        correct,
        incorrect,
        time: elapsedTime
      }]);
    }
    
    setCurrentWord(getNextWord());
    setCurrentParagraphIndex(0);
    
    if (tab === "quotes") {
      setParagraphs(generateQuotes());
    } else {
      setParagraphs(
        Array.from({ length: 10 }, () =>
          formatParagraph(randomWords({ min: 20, max: 40, join: " " }))
        )
      );
    }
    
    setInput("");
    setCorrect(0);
    setIncorrect(0);
    setTotalChars(0);
    setCorrectChars(0);
    setStreak(0);
    setElapsedTime(0);
    setWpm(0);
    setAccuracy(100);
    setIsActive(false);
    setWrongWords([]);
  };

  const changeMode = (newTab) => {
    setTab(newTab);
    if (newTab === "quotes") {
      setParagraphs(generateQuotes());
    }
    resetPractice();
  };

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen();
      setIsFullscreen(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSubmit();
    } else if (tab === "words" && e.key === " ") {
      e.preventDefault(); // don’t let space sit in the input
      handleSubmit();
    } else if (e.key === "Escape") {
      resetPractice();
    }
  };

  const textSizeClass = {
    xs: "text-xs",
    sm: "text-sm",
    md: "text-md",
    lg: "text-lg",
    xl: "text-xl",
    "2xl": "text-2xl"
  };

  return (
    <div className={`font-jarkarta min-h-screen ${theme === "dark" ? "bg-gray-900 text-gray-100" : "bg-gray-50 text-gray-900"} flex flex-col transition-colors duration-300`}>
      {/* Header */}
      <div className="flex justify-between items-center bg-black px-3 py-1">
        <div className="flex items-center gap-1">
          <img src={wordsprintwhite} className="w-2 h-4" alt="favicon" />
          <span className="text-gray-50 text-xs tracking-wider font-dmserif">Word Sprint</span>
        </div>
        <div className="flex gap-1">
          <select 
            value={textSize} 
            onChange={(e) => setTextSize(e.target.value)} 
            className="text-gray-200 bg-gray-900 text-xs border border-gray-500 rounded px-1 hover:font-semibold"
          >
            <option value="xs">XS</option>
            <option value="sm">SM</option>
            <option value="md">MD</option>
            <option value="lg">LG</option>
            <option value="xl">XL</option>
            <option value="2xl">2XL</option>
          </select>

          <select 
            value={theme} 
            onChange={(e) => setTheme(e.target.value)} 
            className="text-gray-200 bg-gray-900 text-xs border border-gray-500 rounded px-1 hover:font-semibold"
          >
            <option value="light">Light</option>
            <option value="dark">Dark</option>
          </select>

          {/* <button onClick={() => setShowTutorial(!showTutorial)} className="text-gray-200 text-xs border border-gray-500 rounded px-1 py-0.5 hover:font-semibold">
            Help
          </button> */}

          <button onClick={home} className="text-gray-200 text-xs border border-gray-500 rounded px-1 py-0.5 hover:font-semibold">
            Home
          </button>
          <button onClick={toggleFullscreen} className="text-gray-200 text-xs border border-gray-500 rounded px-1 py-0.5 hover:font-semibold">
            {isFullscreen ? "Exit Fullscreen" : "Fullscreen"}
          </button>
        </div>
      </div>

      {showTutorial && (
        <div className="bg-blue-50 p-4 border-b border-blue-200">
          <div className="max-w-4xl mx-auto">
            <h3 className="font-bold text-blue-800 mb-2">Typing Practice Tips</h3>
            <ul className="text-sm text-blue-700 list-disc pl-5 space-y-1">
              <li>Press <kbd className="px-1 py-0.5 bg-gray-200 rounded text-xs">Enter</kbd> to submit your answer</li>
              <li>Press <kbd className="px-1 py-0.5 bg-gray-200 rounded text-xs">Escape</kbd> to reset the current session</li>
              <li>Focus on accuracy first, speed will come with practice</li>
              <li>Try to use all fingers for better typing technique</li>
            </ul>
          </div>
        </div>
      )}

      <div className="flex flex-col md:flex-row flex-1 p-2 gap-4">
        {/* Left Panel - Typing Area */}
        <div className={`${theme === "dark" ? "bg-gray-900 border-gray-700" : "bg-white border-gray-200"} rounded-xl p-4 border flex flex-col gap-3 flex-1 transition-colors duration-300`}>
          <div className="flex flex-row gap-1 justify-center text-center text-gray-700 dark:text-gray-200 text-sm sm:text-md">
            <i className="ri-time-line"></i>
            <span>{formatTime(elapsedTime)}</span>
          </div>

          <div className={`rounded-xl p-2 ${textSizeClass[textSize]} ${theme === "dark" ? "text-gray-100" : "text-gray-800"} min-h-[180px] flex items-center justify-center text-center leading-relaxed`}>
            {tab === "words" ? currentWord : paragraphs[currentParagraphIndex]}
          </div>

          <form onSubmit={handleSubmit} className={`flex flex-col gap-0 w-full mx-auto ${tab === 'words' ? "max-w-lg" : ""}`}>
            <div className="block">
              {tab === "words" ?
                <input
                  ref={inputRef}
                  value={input}
                  onChange={handleInputChange}
                  onKeyDown={handleKeyDown}
                  placeholder={tab === "words" ? "Type the word and press Enter..." : "Type the text and press Enter..."}
                  className={`text-center flex h-10 w-full rounded-md border ${theme === "dark" ? "bg-gray-800 border-gray-600 text-white" : "bg-white border-gray-300"} px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground placeholder:font-normal focus-visible:outline-none focus-visible:ring-1 ${checkWords() ? '' : 'focus-visible:ring-red-400'} focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 font-medium transition-colors duration-300`}
                  autoFocus
                />
                :
                <textarea
                  ref={inputRef}
                  value={input}
                  onChange={handleInputChange}
                  onKeyDown={handleKeyDown}
                  placeholder={tab === "words" ? "Type the word and press Enter..." : "Type the text and press Enter..."}
                  className={`placeholder:font-light flex ${textSizeClass[textSize]} ${theme === "dark" ? "bg-gray-800 border-gray-600 text-white" : "bg-white border-gray-300 text-gray-800"} min-h-[180px] h-10 w-full rounded-md border px-3 py-2 ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 ${checkWords() ? '' : 'focus-visible:ring-red-700'} focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 transition-colors duration-300`}
                  autoFocus
                />
              }
              <div className="flex flex-col mt-2">
                <small className={`text-xs md:text-xs ${theme === "dark" ? "text-gray-400" : "text-gray-700"}`}>
                  Hit <span className="font-semibold">Enter↵</span> {tab == 'words' ? 'or' : ''} {tab == 'words' && <span className="font-semibold">Space</span>} to submit • <span className="font-semibold">Esc</span> to reset
              </small>

              <button type="button" onClick={() => setSoundVal(s => !s)} className="border p-1 mt-1 rounded w-fit text-xs">{ soundVal ? 'Turn off sound' : 'Turn on sound' }</button>
              </div>
               {/* <Checkbox labelText="Disable Backspace" value={soundVal} onChange={() => setSoundVal(s => !s)} /> */}
            </div>
          </form>
        </div>

        {/* Right Panel - Stats & Controls */}
        <div className={`border rounded-xl p-2 flex flex-col gap-4 w-full md:w-80 ${theme === "dark" ? "bg-gray-900 border-gray-700" : "bg-white border-gray-200"} transition-colors duration-300`}>
          {/* Tabs */}
          <div className="flex flex-col gap-1">
            <h5 className="font-dmserif text-lg dark:text-gray-100">Practice Mode</h5>
            <div className={`flex p-1 rounded-lg ${theme === "dark" ? "bg-gray-800" : "bg-gray-100"}`}>
              {/* , "quotes" */}
              {["words", "paragraphs"].map(mode => (
                <button
                  key={mode}
                  onClick={() => changeMode(mode)}
                  className={`text-sm md:text-md flex-1 py-1 flex items-center justify-center gap-2 rounded-md ${tab === mode ? (theme === "dark" ? "bg-gray-600 text-white" : "bg-white shadow") : "hover:opacity-80"} transition-colors duration-300 capitalize`}
                >
                  <i className={`ri-${mode === "words" ? "character-recognition" : mode === "paragraphs" ? "file-text" : "double-quotes-l"}-line text-lg`}></i>
                  {mode}
                </button>
              ))}
            </div>
          </div>

          {/* Difficulty Selector */}
          {tab === "words" && (
            <div className="flex flex-col gap-1">
              <h5 className="font-dmserif text-lg dark:text-gray-100">Difficulty Level</h5>
              <div className={`flex p-1 rounded-lg ${theme === "dark" ? "bg-gray-800" : "bg-gray-100"}`}>
                {["easy", "normal", "hard"].map(level => (
                  <button
                    key={level}
                    onClick={() => setDifficulty(level)}
                    className={`text-sm md:text-md flex-1 py-2 text-center rounded-md ${difficulty === level ? (theme === "dark" ? "bg-gray-600 text-white" : "bg-white shadow") : "hover:opacity-80"} transition-colors duration-300 capitalize`}
                  >
                    {level}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Stats Grid */}
          <div className="grid grid-cols-2 gap-3">
            <div className={`p-3 rounded-lg text-center ${theme === "dark" ? "bg-blue-900" : "bg-blue-50"}`}>
              <div className="text-2xl font-bold text-blue-600">{wpm}</div>
              <div className={`text-sm ${theme === "dark" ? "text-blue-200" : "text-blue-800"}`}>WPM</div>
            </div>
            <div className={`p-3 rounded-lg text-center ${theme === "dark" ? "bg-green-900" : "bg-green-50"}`}>
              <div className="text-2xl font-bold text-green-600">{accuracy}%</div>
              <div className={`text-sm ${theme === "dark" ? "text-green-200" : "text-green-800"}`}>Accuracy</div>
            </div>
            <div className={`p-3 rounded-lg text-center ${theme === "dark" ? "bg-yellow-900" : "bg-yellow-50"}`}>
              <div className="text-2xl font-bold text-yellow-600">{streak}</div>
              <div className={`text-sm ${theme === "dark" ? "text-yellow-200" : "text-yellow-800"}`}>Streak</div>
            </div>
            <div className={`p-3 rounded-lg text-center ${theme === "dark" ? "bg-purple-900" : "bg-purple-50"}`}>
              <div className="text-2xl font-bold text-purple-600">{maxStreak}</div>
              <div className={`text-sm ${theme === "dark" ? "text-purple-200" : "text-purple-800"}`}>Best Streak</div>
            </div>
          </div>

          {/* Detailed Results */}
          <div className={`rounded-lg p-3 ${theme === "dark" ? "bg-gray-800" : "bg-gray-50"}`}>
            <h5 className="font-dmserif text-xl mb-2">Session Details</h5>
            <div className="space-y-1.5 text-sm">
              <div className="flex justify-between">
                <span>Time Elapsed:</span>
                <span className="font-medium">{formatTime(elapsedTime)}</span>
              </div>
              <div className="flex justify-between">
                <span>Correct Words:</span>
                <span className="font-medium text-green-700">{correct}</span>
              </div>
              <div className="flex justify-between">
                <span>Errors:</span>
                <span className="font-medium text-red-700">{incorrect}</span>
              </div>
              <div className="flex justify-between">
                <span>Total Words:</span>
                <span className="font-medium">{correct + incorrect}</span>
              </div>
              <div className="flex justify-between">
                <span>Characters:</span>
                <span className="font-medium">{correctChars}/{totalChars}</span>
              </div>
            </div>
          </div>

          {/* Control Buttons */}
          <div className="flex gap-2">
            <button onClick={resetPractice}
              className="flex gap-1 flex-1 justify-center items-center px-3 py-2 text-sm border text-white bg-gray-800 rounded-lg transition hover:bg-gray-800">
              <i className="ri-reset-left-line"></i>
              <span>Reset Session</span>
            </button>
            
            {/* <button onClick={() => setShowTutorial(!showTutorial)}
              className="flex gap-1 justify-center items-center px-3 py-2 text-sm border text-white bg-blue-600 rounded-lg transition hover:bg-blue-700">
              <i className="ri-information-line"></i>
              <span>Tips</span>
            </button> */}
          </div>

          {/* Session History */}
          {sessionHistory.length > 0 && (
            <div className={`rounded-lg p-3 ${theme === "dark" ? "bg-gray-800" : "bg-gray-50"} mt-2`}>
              <h5 className="font-dmserif text-xl mb-2">Recent Sessions</h5>
              <div className="space-y-2 text-xs max-h-32 overflow-y-auto">
                {sessionHistory.slice(-3).reverse().map((session, index) => (
                  <div key={index} className="flex justify-between items-center p-2 rounded bg-white bg-opacity-50">
                    <div>
                      <div>{session.date}</div>
                      <div>{formatTime(session.time)}</div>
                    </div>
                    <div className="text-right">
                      <div className="font-bold">{session.wpm} WPM</div>
                      <div>{session.accuracy}% accuracy</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default EndlessPractice;